// @ts-nocheck
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Eye, EyeOff } from 'lucide-react';
import { supabase } from '@/lib/supabase';

const parentSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email address'),
  username: z.string().min(3, 'Username must be at least 3 characters'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  phone: z.string().optional(),
});

type ParentFormData = z.infer<typeof parentSchema>;

interface MobileParentFormProps {
  onBack: () => void;
  onSuccess?: () => void;
}

const MobileParentForm: React.FC<MobileParentFormProps> = ({ onBack, onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<ParentFormData>({
    resolver: zodResolver(parentSchema)
  });

  const onSubmit = async (data: ParentFormData) => {
    setIsLoading(true);

    try {
      // Check if username is taken
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('username')
        .eq('username', data.username.toLowerCase())
        .single();

      if (existingProfile) {
        toast.error('Username is already taken. Please choose a different one.');
        setIsLoading(false);
        return;
      }

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            first_name: data.firstName,
            last_name: data.lastName,
            full_name: `${data.firstName} ${data.lastName}`,
            username: data.username.toLowerCase(),
            phone: data.phone || '',
            role: 'parent'
          }
        }
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('Failed to create user account');

      const profileData = {
        id: authData.user.id,
        user_id: authData.user.id,
        name: `${data.firstName} ${data.lastName}`,
        email: data.email,
        username: data.username.toLowerCase(),
        phone: data.phone || null,
        role: 'parent',
        primary_role_slug: 'parent',
        active_membership_slug: 'parent_free',
        membership_level: 'free',
        created_at: new Date().toISOString()
      };

      const { error: profileError } = await supabase
        .from('profiles')
        .upsert(profileData, { onConflict: 'user_id', ignoreDuplicates: false })
        .select('*')
        .single();

      if (profileError) throw profileError;

      // RLevents migration: role_assignments table dropped — skip role insert.
      // (Roles are managed via user_roles in Supabase admin.)

      // RLE has no membership plans; nothing to assign.

      toast.success('Parent account created successfully!');
      onSuccess?.();
    } catch (error: any) {
      let errorMessage = 'Failed to create account. Please try again.';
      if (error.message?.includes('duplicate key') && error.message?.includes('username')) {
        errorMessage = 'This username is already taken.';
      } else if (error.message?.includes('duplicate key') && error.message?.includes('email')) {
        errorMessage = 'An account with this email already exists.';
      }
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b border-border p-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Back</span>
        </button>
        <h1 className="text-xl font-bold mt-2">Create Parent Account</h1>
        <p className="text-sm text-muted-foreground">Support your athlete's recruiting journey</p>
      </div>

      {/* Form */}
      <div className="flex-1 p-4 pb-24 overflow-y-auto">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* First Name */}
          <div className="space-y-2">
            <Label htmlFor="firstName">First Name *</Label>
            <Input
              id="firstName"
              placeholder="Enter first name"
              {...register('firstName')}
              disabled={isLoading}
              className="h-12"
            />
            {errors.firstName && <p className="text-sm text-destructive">{errors.firstName.message}</p>}
          </div>

          {/* Last Name */}
          <div className="space-y-2">
            <Label htmlFor="lastName">Last Name *</Label>
            <Input
              id="lastName"
              placeholder="Enter last name"
              {...register('lastName')}
              disabled={isLoading}
              className="h-12"
            />
            {errors.lastName && <p className="text-sm text-destructive">{errors.lastName.message}</p>}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter email address"
              {...register('email')}
              disabled={isLoading}
              className="h-12"
            />
            {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
          </div>

          {/* Username */}
          <div className="space-y-2">
            <Label htmlFor="username">Username *</Label>
            <Input
              id="username"
              placeholder="Choose a username"
              {...register('username')}
              disabled={isLoading}
              className="h-12"
            />
            {errors.username && <p className="text-sm text-destructive">{errors.username.message}</p>}
          </div>

          {/* Password */}
          <div className="space-y-2">
            <Label htmlFor="password">Password *</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Create a password"
                {...register('password')}
                disabled={isLoading}
                className="h-12 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {errors.password && <p className="text-sm text-destructive">{errors.password.message}</p>}
          </div>

          {/* Phone (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number (Optional)</Label>
            <Input
              id="phone"
              type="tel"
              placeholder="Enter phone number"
              {...register('phone')}
              disabled={isLoading}
              className="h-12"
            />
            {errors.phone && <p className="text-sm text-destructive">{errors.phone.message}</p>}
          </div>

          {/* Submit Button */}
          <Button 
            type="submit" 
            className="w-full h-12 bg-secondary hover:bg-secondary/90 text-secondary-foreground font-bold"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating Account...
              </>
            ) : (
              'Create Parent Account'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default MobileParentForm;